import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { superAdminAPI } from "../../services/api";
import {
  sanitizePhoneInput,
  validateOptionalPhone,
  sanitizePincodeInput,
  validateZipCode,
} from "../../utils/validation";
import toast from "react-hot-toast";
import { HiOutlineMail } from "react-icons/hi";
import OrganizationsApiKeys from "./OrganizationsApiKeys";
import PasswordInput from "../../components/SuperAdmin/PasswordInput";
import { buildPageList } from "../../utils/buildPageList";
import { ExportMenu, DateRangeFilter, ResetFiltersButton } from "../../components/SuperAdmin/ListTools";
import { fetchAllPages, dateRangeParams, formatExportDate } from "../../utils/exportData";
import "./OrganizationsList.scss";

// ── Plan rules — same as the backend (utils/billingCycle.computeSubscriptionDates)
// Free → active, no dates · 3-Day / 5-Day Trial → 'trial' ending after trialDays
// · paid (Full Access) → active for one billing cycle (monthly/yearly), no trial
const planKindOf = (p) =>
  !p ? null
    : (p.isPerUser || p.price?.monthly > 0 || p.price?.yearly > 0) ? "paid"
    : p.trialDays > 0 ? "trial" : "free";

/** Per-user monthly rate for a cycle: monthly ₹599, yearly ₹299 (billed 12 months). */
const perUserRate = (plan, cycle) =>
  plan?.isPerUser
    ? (cycle === "yearly" ? plan.pricePerUser?.yearly : plan.pricePerUser?.monthly) || 0
    : (cycle === "yearly" ? plan?.price?.yearly : plan?.price?.monthly) || 0;

/** Local date → "YYYY-MM-DD" for <input type="date">. */
const toDateInput = (d) => {
  if (!d) return "";
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};

/**
 * Form dates + status for a plan / billing cycle, starting at `start`
 * ("YYYY-MM-DD", default today).
 */
const computeFormDates = (plan, cycle, start) => {
  const kind = planKindOf(plan);
  if (!kind || kind === "free") {
    return { status: "active", subscriptionStartsAt: "", subscriptionEndsAt: "", trialEndsAt: "" };
  }
  const startDate = start ? new Date(`${start}T00:00:00`) : new Date();
  const end = new Date(startDate);
  if (kind === "trial") {
    end.setDate(end.getDate() + (plan.trialDays || 0));
    return {
      status: "trial",
      subscriptionStartsAt: toDateInput(startDate),
      subscriptionEndsAt: toDateInput(end),
      trialEndsAt: toDateInput(end),
    };
  }
  if (cycle === "yearly") end.setFullYear(end.getFullYear() + 1);
  else end.setMonth(end.getMonth() + 1);
  return {
    status: "active",
    subscriptionStartsAt: toDateInput(startDate),
    subscriptionEndsAt: toDateInput(end),
    trialEndsAt: "",
  };
};

const EMPTY_ORG_FILTERS = {
  status: "",
  search: "",
  plan: "",
  billingCycle: "",
  source: "",
  from: "",
  to: "",
  expiryFrom: "",
  expiryTo: "",
};

/** Filter state → API params (date inputs are local "YYYY-MM-DD"; the API takes ISO day boundaries). */
const orgApiParams = ({ from, to, expiryFrom, expiryTo, ...rest }) => {
  const expiry = dateRangeParams(expiryFrom, expiryTo);
  return {
    ...rest,
    ...dateRangeParams(from, to),
    ...(expiry.from && { expiryFrom: expiry.from }),
    ...(expiry.to && { expiryTo: expiry.to }),
  };
};

const OrganizationsList = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ ...EMPTY_ORG_FILTERS, page: 1, limit: 20 });
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  const filtersActive = Object.keys(EMPTY_ORG_FILTERS).some((k) => filters[k]);
  const [showModal, setShowModal] = useState(false);
  // Custom confirm dialog (replaces window.confirm) — { title, message, confirmLabel, danger, onConfirm }
  const [confirmModal, setConfirmModal] = useState(null);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [viewingOrg, setViewingOrg] = useState(null);
  const [orgForAdmin, setOrgForAdmin] = useState(null);
  const [orgForRenew, setOrgForRenew] = useState(null);
  const [renewData, setRenewData] = useState({
    months: 1,
    days: 0,
    periodEnd: "",
  });
  const [renewMode, setRenewMode] = useState("months"); // 'months', 'days', 'custom'
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    email: "",
    phone: "",
    planId: "",
    billingCycle: "monthly", // Paid plans: monthly (₹599/user/mo) or yearly (₹299/user/mo)
    numUsers: 1, // How many users (seats) are assigned for this plan
    autoDailyLimit: 10, // Direct (automatic) call analyses allowed per day
    status: "trial",
    address: { street: "", city: "", state: "", country: "", zipCode: "" },
    adminUser: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phone: "",
    },
  });
  const [adminFormData, setAdminFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
  });
  const [formError, setFormError] = useState("");
  // Edit form: dates were recalculated after a plan / billing cycle change
  const [datesAutoUpdated, setDatesAutoUpdated] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  // OTP verification state for admin email (org creation)
  const [adminEmailVerified, setAdminEmailVerified] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["organizations", filters],
    queryFn: () => superAdminAPI.getOrganizations(orgApiParams(filters)),
  });

  const { data: plansData } = useQuery({
    queryKey: ["plans"],
    queryFn: superAdminAPI.getPlans,
  });

  const organizations = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};
  const plans = plansData?.data?.data || [];
  // Plan dropdown: active plans only (in display order). When editing an org
  // still on an inactive plan, keep that one plan so the form keeps its value.
  const planOptions = plans
    .filter((p) => p.isActive !== false || (editingOrg && p._id === formData.planId))
    .sort((a, b) => (a.displayOrder ?? 99) - (b.displayOrder ?? 99));

  const createMutation = useMutation({
    mutationFn: superAdminAPI.createOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries(["organizations"]);
      closeModal();
    },
    onError: (error) =>
      setFormError(
        error.response?.data?.message || "Failed to create organization",
      ),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => superAdminAPI.updateOrganization(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["organizations"]);
      toast.success(
        response?.data?.admin
          ? "Organization and admin credentials updated"
          : "Organization updated",
      );
      closeModal();
    },
    onError: (error) =>
      setFormError(
        error.response?.data?.message || "Failed to update organization",
      ),
  });

  const suspendMutation = useMutation({
    mutationFn: superAdminAPI.suspendOrganization,
    onSuccess: () => queryClient.invalidateQueries(["organizations"]),
  });

  const activateMutation = useMutation({
    mutationFn: superAdminAPI.activateOrganization,
    onSuccess: () => queryClient.invalidateQueries(["organizations"]),
  });

  const deleteMutation = useMutation({
    mutationFn: superAdminAPI.deleteOrganization,
    onSuccess: (response) => {
      queryClient.invalidateQueries(["organizations"]);
      const data = response?.data?.data;
      const totalRecords = data?.totalRecords || 0;
      toast.success(
        `Organization deleted successfully${totalRecords > 0 ? ` (${totalRecords} records removed)` : ""}`,
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete organization",
      );
    },
  });

  // Linked Workspaces (docs/LINKED_WORKSPACES_PLAN.md) — DISABLED for now,
  // feature planned for later. Commented out, not removed, per explicit request.
  // const [linkWorkspaceOrg, setLinkWorkspaceOrg] = useState(null);
  // const [linkWorkspaceTarget, setLinkWorkspaceTarget] = useState("");
  // const [linkWorkspaceCandidates, setLinkWorkspaceCandidates] = useState([]);

  // const setBillingParentMutation = useMutation({
  //   mutationFn: ({ id, billingParentOrganizationId }) =>
  //     superAdminAPI.setBillingParent(id, billingParentOrganizationId),
  //   onSuccess: (response) => {
  //     queryClient.invalidateQueries(["organizations"]);
  //     toast.success(response.data?.message || "Updated");
  //     setLinkWorkspaceOrg(null);
  //     setLinkWorkspaceTarget("");
  //   },
  //   onError: (error) => {
  //     toast.error(error.response?.data?.message || "Failed to update billing parent");
  //   },
  // });

  // const openLinkWorkspaceModal = async (org) => {
  //   setLinkWorkspaceOrg(org);
  //   // billingParentOrganizationId is populated (an object) in the list response —
  //   // the <select> needs the plain id string.
  //   setLinkWorkspaceTarget(org.billingParentOrganizationId?._id || org.billingParentOrganizationId || "");
  //   try {
  //     const res = await superAdminAPI.getOrganizations({ limit: 500 });
  //     const all = res.data?.data || [];
  //     // Eligible parents: not itself, and not already a workspace of another org
  //     setLinkWorkspaceCandidates(
  //       all.filter((o) => o._id !== org._id && !o.billingParentOrganizationId)
  //     );
  //   } catch (err) {
  //     toast.error("Failed to load organizations");
  //     setLinkWorkspaceCandidates([]);
  //   }
  // };

  const renewMutation = useMutation({
    mutationFn: ({ id, data }) => superAdminAPI.renewSubscription(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["organizations"]);
      closeRenewModal();
    },
    onError: (error) =>
      setFormError(
        error.response?.data?.message || "Failed to renew subscription",
      ),
  });

  const createAdminMutation = useMutation({
    mutationFn: ({ orgId, data }) => superAdminAPI.createOrgAdmin(orgId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["organizations"]);
      closeAdminModal();
    },
    onError: (error) =>
      setFormError(error.response?.data?.message || "Failed to create admin"),
  });

  const sendEmailMutation = useMutation({
    // Sends one email per admin sequentially on the server — allow more time
    // than the default 60s so larger org lists don't time out client-side
    mutationFn: (payload) =>
      api.post("/superadmin/admins/send-email", payload, { timeout: 180000 }),
    onSuccess: (response) => {
      const data = response.data?.data || response.data || {};
      const sent = data.sent || 0;
      const failed = data.failed || 0;
      if (failed > 0) {
        // Partial failure — show the real reason reported by the server
        const reason = data.reasons?.[0] || data.errors?.[0]?.error;
        toast(
          `Email sent to ${sent} admin${sent !== 1 ? "s" : ""}. ${failed} failed${reason ? `: ${reason}` : "."}`,
          { icon: "⚠️", duration: 10000 },
        );
      } else {
        toast.success(
          `Email sent to ${sent} admin${sent !== 1 ? "s" : ""} successfully!`,
        );
      }
      setShowEmailModal(false);
      setEmailSubject("");
      setEmailBody("");
    },
    onError: (error) => {
      // All sends failed / validation error — server message carries the reason
      toast.error(error.response?.data?.message || "Failed to send email", {
        duration: 10000,
      });
    },
  });

  // Resend OTP timer countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Send OTP to admin email
  const handleSendAdminOtp = async () => {
    const email = formData.adminUser.email.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError("Please enter a valid admin email address");
      return;
    }

    setOtpSending(true);
    setOtpError("");
    try {
      const res = await superAdminAPI.sendOrgAdminOTP(email);
      if (res.data?.success) {
        setShowOtpModal(true);
        setOtp("");
        setResendTimer(60);
        toast.success("OTP sent to admin email address");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to send OTP. Please try again.";
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setOtpSending(false);
    }
  };

  // Verify OTP
  const handleVerifyAdminOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Please enter the 6-digit OTP");
      return;
    }

    setOtpVerifying(true);
    setOtpError("");
    try {
      const email = formData.adminUser.email.trim().toLowerCase();
      const res = await superAdminAPI.verifyOrgAdminOTP(email, otp);
      if (res.data?.success) {
        setAdminEmailVerified(true);
        setShowOtpModal(false);
        setFormError("");
        toast.success("Email verified successfully!");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || "Invalid OTP. Please try again.";
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setOtpVerifying(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingOrg(null);
    setFormError("");
    setDatesAutoUpdated(false);
    setFormData({
      name: "",
      slug: "",
      email: "",
      phone: "",
      planId: "",
      billingCycle: "monthly",
      numUsers: 1, // How many users (seats) are assigned for this plan
      autoDailyLimit: 10, // Direct (automatic) call analyses allowed per day
      status: "trial",
      address: { street: "", city: "", state: "", country: "", zipCode: "" },
      adminUser: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        phone: "",
      },
    });
    // Reset OTP verification state
    setAdminEmailVerified(false);
    setShowOtpModal(false);
    setOtp("");
    setOtpError("");
    setResendTimer(0);
  };

  const closeAdminModal = () => {
    setShowAdminModal(false);
    setOrgForAdmin(null);
    setFormError("");
    setAdminFormData({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phone: "",
    });
  };

  const closeRenewModal = () => {
    setShowRenewModal(false);
    setOrgForRenew(null);
    setFormError("");
    setRenewData({ months: 1, days: 0, periodEnd: "" });
    setRenewMode("months");
  };

  const openCreateAdminModal = (org) => {
    setOrgForAdmin(org);
    setShowAdminModal(true);
  };

  const openRenewModal = (org) => {
    setOrgForRenew(org);
    setRenewMode("months");
    setRenewData({ months: 1, days: 0, periodEnd: "" });
    setShowRenewModal(true);
  };

  const openEditModal = (org) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      slug: org.slug,
      email: org.email,
      phone: org.phone || "",
      planId: org.planId?._id || org.planId || "",
      billingCycle: org.billingCycle || "monthly",
      numUsers: org.paidSeats || 1,
      autoDailyLimit: org.callAnalysis?.autoDailyLimit ?? 10,
      status: org.status,
      address: org.address || {
        street: "",
        city: "",
        state: "",
        country: "",
        zipCode: "",
      },
      // Prefill the org admin's details (password is never shown — it is
      // hashed; leaving it blank keeps the current one)
      adminUser: {
        firstName: org.adminId?.firstName || "",
        lastName: org.adminId?.lastName || "",
        email: org.adminId?.email || "",
        password: "",
        phone:
          org.adminId?.phones?.find((p) => p.isDefault)?.number ||
          org.adminId?.phones?.[0]?.number ||
          "",
      },
      subscriptionStartsAt: toDateInput(org.subscriptionStartsAt),
      subscriptionEndsAt: toDateInput(org.subscriptionEndsAt),
      trialEndsAt: toDateInput(org.trialEndsAt),
    });
    setDatesAutoUpdated(false);
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Sanitize phone and zipCode inputs
    let sanitizedValue = value;
    if (name === "phone" || name === "adminUser.phone") {
      sanitizedValue = sanitizePhoneInput(value);
    } else if (name === "address.zipCode") {
      sanitizedValue = sanitizePincodeInput(value, 10);
    }
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: { ...prev.address, [field]: sanitizedValue },
      }));
    } else if (name.startsWith("adminUser.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        adminUser: { ...prev.adminUser, [field]: sanitizedValue },
      }));
      // Reset email verification when admin email changes
      if (field === "email") {
        setAdminEmailVerified(false);
      }
    } else if (editingOrg && (name === "planId" || name === "billingCycle")) {
      // Plan or billing cycle changed while editing → recalculate the period.
      // A new plan starts today; a cycle switch on the same plan keeps the
      // current start date. The dates stay editable afterwards.
      setFormData((prev) => {
        const next = { ...prev, [name]: sanitizedValue };
        const plan = plans.find((p) => p._id === next.planId);
        const keepStart = name === "billingCycle" && prev.subscriptionStartsAt;
        return {
          ...next,
          ...computeFormDates(plan, next.billingCycle, keepStart ? prev.subscriptionStartsAt : undefined),
        };
      });
      setDatesAutoUpdated(true);
    } else {
      setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
      if (name === "name" && !editingOrg) {
        const slug = sanitizedValue
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
        setFormData((prev) => ({ ...prev, slug }));
      }
    }
  };

  const handleAdminInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = name === "phone" ? sanitizePhoneInput(value) : value;
    setAdminFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
  };

  // Edit mode: the org's current admin user (populated by the list API)
  const editingOrgAdmin =
    editingOrg?.adminId && typeof editingOrg.adminId === "object"
      ? editingOrg.adminId
      : null;
  const adminEmailChanged =
    !!editingOrgAdmin &&
    formData.adminUser.email.trim().toLowerCase() !==
      String(editingOrgAdmin.email || "").toLowerCase();
  // Create: always verify. Edit: verify only when the admin email changes.
  const needsAdminEmailVerification = !editingOrg || adminEmailChanged;

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");

    if (
      !formData.name ||
      !formData.slug ||
      !formData.email ||
      !formData.planId
    ) {
      setFormError("Please fill in all required organization fields");
      return;
    }

    // Validate organization phone if provided
    if (formData.phone && formData.phone.trim()) {
      const phoneValidation = validateOptionalPhone(formData.phone);
      if (!phoneValidation.isValid) {
        setFormError(`Organization phone: ${phoneValidation.error}`);
        return;
      }
    }

    // Validate zip code if provided
    if (formData.address?.zipCode && formData.address.zipCode.trim()) {
      const zipValidation = validateZipCode(formData.address.zipCode);
      if (!zipValidation.isValid) {
        setFormError(zipValidation.error);
        return;
      }
    }

    // Validate number of users (seats) for the selected plan
    const numUsers = parseInt(formData.numUsers) || 0;
    if (numUsers < 1) {
      setFormError("Number of users must be at least 1");
      return;
    }
    if (selectedPlan?.limits?.users > 0 && numUsers > selectedPlan.limits.users) {
      setFormError(
        `The "${selectedPlan.name}" plan allows a maximum of ${selectedPlan.limits.users} users. Please upgrade the plan or reduce the number of users.`,
      );
      return;
    }

    if (!editingOrg) {
      const { firstName, lastName, email, password } = formData.adminUser;
      if (!firstName || !lastName || !email || !password) {
        setFormError("Please fill in all required admin user fields");
        return;
      }
      // Email verification is required for new org admin
      if (!adminEmailVerified) {
        setFormError(
          "Please verify the admin email address before creating the organization",
        );
        return;
      }
      if (password.length < 8) {
        setFormError("Password must be at least 8 characters");
        return;
      }
      // Validate admin phone if provided
      if (formData.adminUser.phone && formData.adminUser.phone.trim()) {
        const adminPhoneValidation = validateOptionalPhone(
          formData.adminUser.phone,
        );
        if (!adminPhoneValidation.isValid) {
          setFormError(`Admin phone: ${adminPhoneValidation.error}`);
          return;
        }
      }
    }

    if (editingOrg && editingOrgAdmin) {
      const { firstName, lastName, email, password, phone } =
        formData.adminUser;
      if (!firstName.trim() || !lastName.trim() || !email.trim()) {
        setFormError("Admin first name, last name and email are required");
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setFormError("Please enter a valid admin email address");
        return;
      }
      if (adminEmailChanged && !adminEmailVerified) {
        setFormError(
          "Please verify the new admin email address before saving",
        );
        return;
      }
      if (password && password.length < 8) {
        setFormError("New admin password must be at least 8 characters");
        return;
      }
      if (phone && phone.trim()) {
        const adminPhoneValidation = validateOptionalPhone(phone);
        if (!adminPhoneValidation.isValid) {
          setFormError(`Admin phone: ${adminPhoneValidation.error}`);
          return;
        }
      }
    }

    if (editingOrg) {
      // Backend PATCH expects `paidSeats` for updating seat count
      const { adminUser, ...orgData } = formData;
      updateMutation.mutate({
        id: editingOrg._id,
        data: {
          ...orgData,
          paidSeats: numUsers,
          // Only send admin changes when the org has an admin user
          ...(editingOrgAdmin
            ? {
                adminUser,
                adminUserEmailVerified: adminEmailChanged
                  ? adminEmailVerified
                  : undefined,
              }
            : {}),
        },
      });
    } else {
      // Backend POST expects `numUsers` for initial seat count
      createMutation.mutate({
        ...formData,
        numUsers,
        adminUserEmailVerified: true,
      });
    }
  };

  const handleAdminSubmit = (e) => {
    e.preventDefault();
    setFormError("");

    const { firstName, lastName, email, password } = adminFormData;
    if (!firstName || !lastName || !email || !password) {
      setFormError("Please fill in all required fields");
      return;
    }
    if (password.length < 8) {
      setFormError("Password must be at least 8 characters");
      return;
    }

    createAdminMutation.mutate({ orgId: orgForAdmin._id, data: adminFormData });
  };

  const handleRenewSubmit = (e) => {
    e.preventDefault();
    setFormError("");

    let payload = {};
    if (renewMode === "months") {
      if (!renewData.months || renewData.months < 1) {
        setFormError("Please enter a valid number of months");
        return;
      }
      payload = { months: parseInt(renewData.months) };
    } else if (renewMode === "days") {
      if (!renewData.days || renewData.days < 1) {
        setFormError("Please enter a valid number of days");
        return;
      }
      payload = { days: parseInt(renewData.days) };
    } else {
      if (!renewData.periodEnd) {
        setFormError("Please select an end date");
        return;
      }
      payload = { periodEnd: renewData.periodEnd };
    }

    renewMutation.mutate({ id: orgForRenew._id, data: payload });
  };

  const handleSuspend = (orgId, orgName) => {
    setConfirmModal({
      title: "Suspend Organization",
      message: `Are you sure you want to suspend "${orgName}"? This will block all access for this organization.`,
      confirmLabel: "Suspend",
      danger: true,
      onConfirm: () => suspendMutation.mutate(orgId),
    });
  };

  // Direct login — Super Admin signs into an organization's admin panel as
  // its admin WITHOUT the admin's password. Backend issues the admin's own
  // JWT (same shape as /auth/admin-login); we store it and reload the app.
  const handleDirectLogin = (org) => {
    setConfirmModal({
      title: "Direct Login",
      message: `Log into "${org.name}" admin panel as its admin without a password?`,
      confirmLabel: "Log In",
      onConfirm: () => performDirectLogin(org),
    });
  };

  const performDirectLogin = async (org) => {
    toast.loading("Signing you in as admin...", { id: "direct-login" });
    try {
      const res = await superAdminAPI.loginAsAdmin(org._id);
      const data = res.data?.data || {};
      if (!data.token || !data.user) {
        throw new Error("Invalid response from server");
      }
      localStorage.setItem("admin_auth_token", data.token);
      localStorage.setItem("admin_user_data", JSON.stringify(data.user));
      toast.success(`Logged into ${org.name} admin panel`, {
        id: "direct-login",
      });
      // Full reload so AuthContext picks up the admin token from localStorage
      window.location.href = "/app/";
    } catch (err) {
      toast.dismiss("direct-login");
      toast.error(
        err.response?.data?.message || err.message || "Direct login failed",
      );
    }
  };

  const handleActivate = (orgId, orgName) => {
    setConfirmModal({
      title: "Activate Organization",
      message: `Are you sure you want to activate "${orgName}"?`,
      confirmLabel: "Activate",
      onConfirm: () => activateMutation.mutate(orgId),
    });
  };

  const handleDelete = (orgId, orgName) => {
    setConfirmModal({
      title: "Delete Organization",
      message: `Are you sure you want to permanently delete "${orgName}"? This will delete ALL data including users, leads, call logs, recordings, and settings. This action CANNOT be undone.`,
      confirmLabel: "Delete Permanently",
      danger: true,
      onConfirm: () => deleteMutation.mutate(orgId),
    });
  };

  const getStatusBadge = (status) => {
    const classes = {
      trial: "badge-warning",
      active: "badge-success",
      suspended: "badge-danger",
      cancelled: "badge-secondary",
    };
    return classes[status] || "badge-secondary";
  };

  // Compute subscription expiry info for an org
  const getSubscriptionInfo = (org) => {
    const now = new Date();
    const endDate = org.subscriptionEndsAt || org.trialEndsAt;
    const trialEnd = org.trialEndsAt;

    let isExpired = org.status === "suspended" || org.status === "cancelled";
    let expireReason = null;
    let daysRemaining = null;

    if (org.status === "trial" && trialEnd && new Date(trialEnd) < now) {
      isExpired = true;
      expireReason = "Trial expired";
    } else if (org.status === "active" && endDate && new Date(endDate) < now) {
      isExpired = true;
      expireReason = "Subscription expired";
    }

    if (endDate) {
      daysRemaining = Math.max(
        0,
        Math.ceil((new Date(endDate) - now) / (1000 * 60 * 60 * 24)),
      );
    }

    return { isExpired, expireReason, daysRemaining, endDate };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Subscription end preview — same rules as the backend
  // (utils/billingCycle.computeSubscriptionDates): Free → no end date,
  // 3-Day / 5-Day Trial → ends after trialDays, paid → one billing cycle, no trial
  const selectedPlan = plans.find((p) => p._id === formData.planId);
  const planKind = planKindOf;
  const isPaidSelected = planKind(selectedPlan) === "paid";
  const cycleRate = perUserRate(selectedPlan, formData.billingCycle);
  const cycleMonths = formData.billingCycle === "yearly" ? 12 : 1;
  const subscriptionPreview =
    selectedPlan && !editingOrg && planKind(selectedPlan) !== 'free'
      ? (() => {
          const end = new Date();
          if (planKind(selectedPlan) === 'trial') {
            end.setDate(end.getDate() + selectedPlan.trialDays);
          } else if (formData.billingCycle === 'yearly') {
            end.setFullYear(end.getFullYear() + 1);
          } else {
            end.setMonth(end.getMonth() + 1);
          }
          return formatDate(end.toISOString());
        })()
      : null;

  // Number of users (seats) preview for the selected plan
  const seatsCount = parseInt(formData.numUsers) || 0;
  const planUserLimit = selectedPlan?.limits?.users ?? -1; // -1 = unlimited
  const seatsHint =
    !selectedPlan
      ? "Select a plan to see its user allowance"
      : selectedPlan.isPerUser
        ? `👥 ${seatsCount} user(s) × ₹${cycleRate}/user/mo${cycleMonths === 12 ? ' × 12 months' : ''} = ₹${(cycleRate * seatsCount * cycleMonths).toLocaleString('en-IN')}${cycleMonths === 12 ? '/year' : '/month'} + GST (admin account is free)`
        : planUserLimit === -1
          ? "♾️ This plan allows unlimited users"
          : `👥 This plan allows up to ${planUserLimit} user(s)`;

  return (
    <div className="organizations-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Organizations</h1>
          <p>Manage all organizations on the platform</p>
        </div>
        <div className="header-right">
          <button
            className="btn btn-outline"
            onClick={() => setShowEmailModal(true)}
            style={{ marginRight: "8px" }}
          >
            ✉️ Send Email to Admins
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            + Add Organization
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search name, email, slug, phone..."
          value={filters.search}
          onChange={(e) => setFilter({ search: e.target.value })}
          className="search-input"
        />
        <select
          value={filters.status}
          onChange={(e) => setFilter({ status: e.target.value })}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="trial">Trial</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={filters.plan}
          onChange={(e) => setFilter({ plan: e.target.value })}
          className="filter-select"
        >
          <option value="">All Plans</option>
          {plans.map((p) => (
            <option key={p._id} value={p._id}>
              {p.name}{p.isActive === false ? " (inactive)" : ""}
            </option>
          ))}
        </select>
        <select
          value={filters.billingCycle}
          onChange={(e) => setFilter({ billingCycle: e.target.value })}
          className="filter-select"
        >
          <option value="">All Billing Cycles</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
        <select
          value={filters.source}
          onChange={(e) => setFilter({ source: e.target.value })}
          className="filter-select"
        >
          <option value="">All Sources</option>
          <option value="direct">Direct</option>
          <option value="channel_partner">Via Channel Partner</option>
        </select>
        <DateRangeFilter
          label="Created"
          from={filters.from}
          to={filters.to}
          onChange={({ from, to }) => setFilter({ from, to })}
        />
        <DateRangeFilter
          label="Expires"
          from={filters.expiryFrom}
          to={filters.expiryTo}
          onChange={({ from, to }) => setFilter({ expiryFrom: from, expiryTo: to })}
        />
        <ResetFiltersButton
          active={filtersActive}
          onReset={() => setFilters((prev) => ({ ...prev, ...EMPTY_ORG_FILTERS, page: 1 }))}
        />
        <ExportMenu
          fileName="organizations"
          getRows={() => fetchAllPages(superAdminAPI.getOrganizations, orgApiParams(filters))}
          columns={() => [
            { header: "Organization", value: (o) => o.name },
            { header: "Slug", value: (o) => o.slug },
            { header: "Email", value: (o) => o.email },
            { header: "Phone", value: (o) => o.phone },
            { header: "Plan", value: (o) => o.planId?.name },
            { header: "Status", value: (o) => o.status },
            { header: "Billing Cycle", value: (o) => o.billingCycle },
            { header: "Subscription Status", value: (o) => {
              const s = getSubscriptionInfo(o);
              if (s.isExpired) return s.expireReason || "Expired";
              return s.daysRemaining !== null ? `${s.daysRemaining} days left` : "Active";
            } },
            { header: "Subscription Start", value: (o) => formatExportDate(o.subscriptionStartsAt, false) },
            { header: "Subscription End", value: (o) => formatExportDate(o.subscriptionEndsAt || o.trialEndsAt, false) },
            { header: "Trial Ends", value: (o) => formatExportDate(o.trialEndsAt, false) },
            { header: "Users", value: (o) => o.usage?.users || 0 },
            { header: "Paid Seats", value: (o) => o.paidSeats || "" },
            { header: "Awaiting Payment", value: (o) => !!o.awaitingPayment },
            { header: "Admin Name", value: (o) => [o.adminId?.firstName, o.adminId?.lastName].filter(Boolean).join(" ") },
            { header: "Admin Email", value: (o) => o.adminId?.email },
            { header: "Admin Phone", value: (o) => o.adminId?.phones?.[0]?.number },
            { header: "Source", value: (o) => (o.channelPartnerId ? "Channel Partner" : "Direct") },
            { header: "City", value: (o) => o.address?.city },
            { header: "State", value: (o) => o.address?.state },
            { header: "Country", value: (o) => o.address?.country },
            { header: "Created", value: (o) => formatExportDate(o.createdAt) },
          ]}
        />
      </div>

      <div className="table-container">
        {isLoading ? (
          <div className="loading">Loading organizations...</div>
        ) : organizations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏢</div>
            <h3>No organizations found</h3>
            <p>Click "Add Organization" to create your first organization</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Subscription</th>
                  <th>Users</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map((org) => {
                  const subInfo = getSubscriptionInfo(org);
                  return (
                    <tr key={org._id}>
                      <td>
                        <div className="org-name">
                          <span className="name">{org.name}</span>
                          <span className="slug">{org.slug}</span>
                          {/* Linked Workspaces disabled — field no longer populated
                          {org.billingParentOrganizationId && (
                            <span
                              className="badge badge-info"
                              style={{ marginTop: "0.25rem", display: "inline-flex", alignItems: "center", gap: "0.25rem", width: "fit-content" }}
                              title={`Billed under ${org.billingParentOrganizationId.name}'s subscription`}
                            >
                              🔗 Workspace of {org.billingParentOrganizationId.name}
                            </span>
                          )}
                          */}
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-info">
                          {org.planId?.name || "Unknown"}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadge(org.status)}`}>
                          {org.status}
                        </span>
                      </td>
                      <td>
                        <div className="subscription-info">
                          {subInfo.isExpired ? (
                            <span className="badge badge-danger">
                              {subInfo.expireReason}
                            </span>
                          ) : subInfo.daysRemaining !== null ? (
                            <div>
                              <span
                                className={`badge ${subInfo.daysRemaining <= 3 ? "badge-warning" : "badge-success"}`}
                              >
                                {subInfo.daysRemaining} day
                                {subInfo.daysRemaining !== 1 ? "s" : ""} left
                              </span>
                              <div className="sub-date">
                                {formatDate(subInfo.endDate)}
                              </div>
                            </div>
                          ) : (
                            <span className="badge badge-success">Active</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {org.usage?.users || 0}
                        {org.paidSeats ? (
                          <span style={{ color: "#8ea1b8" }}>
                            {" "}
                            / {org.paidSeats} seats
                          </span>
                        ) : null}
                      </td>
                      <td>{new Date(org.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={async () => {
                              try {
                                const res = await superAdminAPI.getOrganization(
                                  org._id,
                                );
                                setViewingOrg(res.data?.data || res.data);
                              } catch (err) {
                                toast.error(
                                  "Failed to load organization details",
                                );
                              }
                            }}
                          >
                            View
                          </button>
                          {/* Direct Login — super admin enters this org's
                              admin panel as its admin, no password needed */}
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleDirectLogin(org)}
                            title="Log into this organization's admin panel as its admin — no password required"
                          >
                            Direct Login
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => openEditModal(org)}
                          >
                            Edit
                          </button>
                          {/* Linked Workspaces disabled
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => openLinkWorkspaceModal(org)}
                            title="Link this org as a workspace billed under another org's subscription"
                          >
                            {org.billingParentOrganizationId ? "Workspace ✓" : "Workspace"}
                          </button>
                          */}
                          {subInfo.isExpired && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => openRenewModal(org)}
                              title="Renew subscription"
                            >
                              Renew
                            </button>
                          )}
                          {!subInfo.isExpired &&
                            org.status !== "suspended" &&
                            org.status !== "cancelled" && (
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleSuspend(org._id, org.name)}
                              >
                                Suspend
                              </button>
                            )}
                          {org.status === "suspended" && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleActivate(org._id, org.name)}
                            >
                              Activate
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(org._id, org.name)}
                            disabled={deleteMutation.isPending}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination (server-side: page + limit sent to the API) */}
      {pagination.total > 0 && (
        <div className="pagination">
          <div className="pagination-info">
            <span className="page-info">
              Showing {(pagination.page - 1) * pagination.limit + 1}–
              {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
              of {pagination.total}
            </span>
            <div className="rows-per-page">
              <label htmlFor="orgs-rows-per-page">Rows per page:</label>
              <select
                id="orgs-rows-per-page"
                value={filters.limit}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    limit: Number(e.target.value),
                    page: 1,
                  }))
                }
                className="filter-select filter-select--sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {pagination.totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="btn btn-outline btn-sm"
                disabled={filters.page <= 1}
                onClick={() =>
                  setFilters((prev) => ({ ...prev, page: prev.page - 1 }))
                }
              >
                Previous
              </button>
              <div className="page-buttons">
                {buildPageList(filters.page, pagination.totalPages).map(
                  (item, idx) =>
                    item === "..." ? (
                      <span key={`ellipsis-${idx}`} className="page-ellipsis">
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        className={`btn btn-page${filters.page === item ? " active" : ""}`}
                        onClick={() =>
                          setFilters((prev) => ({ ...prev, page: item }))
                        }
                      >
                        {item}
                      </button>
                    ),
                )}
              </div>
              <button
                className="btn btn-outline btn-sm"
                disabled={filters.page >= pagination.totalPages}
                onClick={() =>
                  setFilters((prev) => ({ ...prev, page: prev.page + 1 }))
                }
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Admin Modal */}
      {showAdminModal && orgForAdmin && (
        <div className="modal-overlay" onClick={closeAdminModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Admin for {orgForAdmin.name}</h2>
              <button className="modal-close" onClick={closeAdminModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleAdminSubmit} className="modal-form">
              {formError && <div className="error-message">{formError}</div>}

              <p className="section-hint" style={{ marginBottom: "1.5rem" }}>
                This user will be the administrator for "{orgForAdmin.name}" and
                can log in to manage the organization.
              </p>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={adminFormData.firstName}
                    onChange={handleAdminInputChange}
                    placeholder="John"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={adminFormData.lastName}
                    onChange={handleAdminInputChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={adminFormData.email}
                    onChange={handleAdminInputChange}
                    placeholder="admin@company.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={adminFormData.phone}
                    onChange={handleAdminInputChange}
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Password *</label>
                <PasswordInput
                  name="password"
                  value={adminFormData.password}
                  onChange={handleAdminInputChange}
                  placeholder="Minimum 8 characters"
                  autoComplete="new-password"
                  required
                />
                <small className="form-hint">
                  Password must be at least 8 characters long
                </small>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={closeAdminModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createAdminMutation.isPending}
                >
                  {createAdminMutation.isPending
                    ? "Creating..."
                    : "Create Admin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Renew Subscription Modal */}
      {showRenewModal && orgForRenew && (
        <div className="modal-overlay" onClick={closeRenewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Renew Subscription — {orgForRenew.name}</h2>
              <button className="modal-close" onClick={closeRenewModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleRenewSubmit} className="modal-form">
              {formError && <div className="error-message">{formError}</div>}

              <div className="renew-info">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "0.5rem",
                  }}
                >
                  <span className="renew-info-label">
                    Current Plan:
                  </span>
                  <span style={{ fontWeight: 500 }}>
                    {orgForRenew.planId?.name || "Unknown"}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "0.5rem",
                  }}
                >
                  <span className="renew-info-label">
                    Status:
                  </span>
                  <span
                    className={`badge ${getStatusBadge(orgForRenew.status)}`}
                  >
                    {orgForRenew.status}
                  </span>
                </div>
                {orgForRenew.subscriptionEndsAt && (
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span className="renew-info-label">
                      Current End Date:
                    </span>
                    <span>{formatDate(orgForRenew.subscriptionEndsAt)}</span>
                  </div>
                )}
              </div>

              <div className="form-section">
                <h4>Renewal Duration</h4>
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    marginBottom: "1rem",
                  }}
                >
                  <button
                    type="button"
                    className={`btn ${renewMode === "months" ? "btn-primary" : "btn-outline"}`}
                    onClick={() => setRenewMode("months")}
                    style={{ flex: 1 }}
                  >
                    Months
                  </button>
                  <button
                    type="button"
                    className={`btn ${renewMode === "days" ? "btn-primary" : "btn-outline"}`}
                    onClick={() => setRenewMode("days")}
                    style={{ flex: 1 }}
                  >
                    Days
                  </button>
                  <button
                    type="button"
                    className={`btn ${renewMode === "custom" ? "btn-primary" : "btn-outline"}`}
                    onClick={() => setRenewMode("custom")}
                    style={{ flex: 1 }}
                  >
                    Custom Date
                  </button>
                </div>

                {renewMode === "months" && (
                  <div className="form-group">
                    <label>Number of Months</label>
                    <input
                      type="number"
                      min="1"
                      max="36"
                      value={renewData.months}
                      onChange={(e) =>
                        setRenewData((prev) => ({
                          ...prev,
                          months: e.target.value,
                        }))
                      }
                      placeholder="1"
                      required
                    />
                    <small className="form-hint">
                      Subscription will be extended by {renewData.months || 1}{" "}
                      month{(renewData.months || 1) !== 1 ? "s" : ""} from{" "}
                      {orgForRenew.subscriptionEndsAt &&
                      new Date(orgForRenew.subscriptionEndsAt) > new Date()
                        ? "current end date"
                        : "today"}
                    </small>
                  </div>
                )}

                {renewMode === "days" && (
                  <div className="form-group">
                    <label>Number of Days</label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={renewData.days}
                      onChange={(e) =>
                        setRenewData((prev) => ({
                          ...prev,
                          days: e.target.value,
                        }))
                      }
                      placeholder="30"
                      required
                    />
                    <small className="form-hint">
                      Subscription will be extended by {renewData.days || 30}{" "}
                      day{(renewData.days || 30) !== 1 ? "s" : ""} from{" "}
                      {orgForRenew.subscriptionEndsAt &&
                      new Date(orgForRenew.subscriptionEndsAt) > new Date()
                        ? "current end date"
                        : "today"}
                    </small>
                  </div>
                )}

                {renewMode === "custom" && (
                  <div className="form-group">
                    <label>New End Date</label>
                    <input
                      type="date"
                      value={renewData.periodEnd}
                      onChange={(e) =>
                        setRenewData((prev) => ({
                          ...prev,
                          periodEnd: e.target.value,
                        }))
                      }
                      min={new Date().toISOString().split("T")[0]}
                      required
                    />
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={closeRenewModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={renewMutation.isPending}
                >
                  {renewMutation.isPending
                    ? "Renewing..."
                    : "Renew Subscription"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Organization Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="modal-content modal-large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                {editingOrg ? "Edit Organization" : "Add New Organization"}
              </h2>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {formError && <div className="error-message">{formError}</div>}

              {/* Organization Details Section */}
              <div className="form-section">
                <h4>Organization Details</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Organization Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Acme Corporation"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Slug *</label>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="acme-corporation"
                      required
                      disabled={!!editingOrg}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Organization Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="contact@acme.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Plan *</label>
                    <select
                      name="planId"
                      value={formData.planId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select a plan</option>
                      {planOptions.map((plan) => (
                        <option key={plan._id} value={plan._id}>
                          {plan.name}{plan.isActive === false ? ' (inactive)' : ''} — {planKind(plan) === 'trial'
                            ? `${plan.trialDays}-day trial`
                            : planKind(plan) === 'free'
                              ? 'free forever'
                              : `₹${perUserRate(plan, 'monthly')}/user/mo monthly · ₹${perUserRate(plan, 'yearly')}/user/mo yearly`}
                        </option>
                      ))}
                    </select>
                    {subscriptionPreview && (
                      <small
                        className="form-hint"
                        style={{ color: "#059669", fontWeight: 500 }}
                      >
                        📅 Subscription period ends: {subscriptionPreview}
                      </small>
                    )}
                  </div>
                  {editingOrg && (
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="trial">Trial</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Billing cycle — paid (per-user) plans only */}
                {isPaidSelected && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Billing Cycle *</label>
                      <select
                        name="billingCycle"
                        value={formData.billingCycle || "monthly"}
                        onChange={handleInputChange}
                      >
                        <option value="monthly">
                          Monthly — ₹{perUserRate(selectedPlan, "monthly")}/user/month
                        </option>
                        <option value="yearly">
                          Yearly — ₹{perUserRate(selectedPlan, "yearly")}/user/month (billed ₹
                          {(perUserRate(selectedPlan, "yearly") * 12).toLocaleString("en-IN")}/user/year)
                        </option>
                      </select>
                      <small className="form-hint">
                        {formData.billingCycle === "yearly"
                          ? "Subscription runs for 1 year from the start date"
                          : "Subscription runs for 1 month from the start date"}
                      </small>
                    </div>
                  </div>
                )}

                {/* Number of Users assigned for this plan */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Number of Users *</label>
                    <input
                      type="number"
                      name="numUsers"
                      min="1"
                      max={planUserLimit === -1 ? undefined : planUserLimit}
                      value={formData.numUsers}
                      onChange={handleInputChange}
                      required
                    />
                    <small
                      className="form-hint"
                      style={
                        planUserLimit > 0 && seatsCount > planUserLimit
                          ? { color: "#dc2626", fontWeight: 500 }
                          : undefined
                      }
                    >
                      {seatsHint}
                      {planUserLimit > 0 && seatsCount > planUserLimit
                        ? ` — ⚠️ exceeds the plan limit of ${planUserLimit}`
                        : ""}
                    </small>
                  </div>
                  {/* Direct (automatic) call analysis daily limit — controlled by superadmin */}
                  <div className="form-group">
                    <label>Direct Call Analysis Limit / Day</label>
                    <input
                      type="number"
                      name="autoDailyLimit"
                      min="0"
                      value={formData.autoDailyLimit}
                      onChange={handleInputChange}
                    />
                    <small className="form-hint">
                      Max calls analyzed automatically per day (0 = disabled). The organization can
                      switch between Manual and Direct modes in their Settings → Calls.
                    </small>
                  </div>
                </div>
              </div>

              {/* Subscription Dates (edit only) */}
              {editingOrg && (
                <div className="form-section">
                  <h4>Subscription Period</h4>
                  {datesAutoUpdated && (
                    <p className="section-hint" style={{ color: "#059669" }}>
                      📅 Dates updated for the selected plan
                      {isPaidSelected ? ` (${formData.billingCycle})` : ""} — you can still adjust them
                      before saving.
                    </p>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Subscription Start Date</label>
                      <input
                        type="date"
                        name="subscriptionStartsAt"
                        value={formData.subscriptionStartsAt || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Trial End Date</label>
                      <input
                        type="date"
                        name="trialEndsAt"
                        value={formData.trialEndsAt || ""}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Subscription End Date</label>
                      <input
                        type="date"
                        name="subscriptionEndsAt"
                        value={formData.subscriptionEndsAt || ""}
                        onChange={handleInputChange}
                      />
                      <small className="form-hint">
                        Set when the organization's paid subscription expires
                      </small>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit mode, org without an admin → point to Create Admin */}
              {editingOrg && !editingOrgAdmin && (
                <div className="form-section admin-section">
                  <h4>Admin User Credentials</h4>
                  <p className="section-hint">
                    This organization has no admin user yet. Use the
                    &quot;Create Admin&quot; action in the organizations list to
                    add one.
                  </p>
                </div>
              )}

              {/* Admin User Section — create, and edit when the org has an admin */}
              {(!editingOrg || editingOrgAdmin) && (
                <div className="form-section admin-section">
                  <h4>Admin User Credentials</h4>
                  <p className="section-hint">
                    {editingOrg
                      ? "Update the organization administrator's login details. Changing the email requires OTP verification of the new address."
                      : "This user will be the administrator for this organization and can log in to manage leads, users, and other data."}
                  </p>

                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        name="adminUser.firstName"
                        value={formData.adminUser.firstName}
                        onChange={handleInputChange}
                        placeholder="John"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        name="adminUser.lastName"
                        value={formData.adminUser.lastName}
                        onChange={handleInputChange}
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Admin Email *</label>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "flex-start",
                        }}
                      >
                        <input
                          type="email"
                          name="adminUser.email"
                          value={formData.adminUser.email}
                          onChange={handleInputChange}
                          placeholder="admin@acme.com"
                          required
                          style={{ flex: 1 }}
                        />
                        {needsAdminEmailVerification && (
                          <button
                            type="button"
                            onClick={handleSendAdminOtp}
                            disabled={
                              otpSending ||
                              !formData.adminUser.email.trim() ||
                              adminEmailVerified
                            }
                            className="px-3 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              backgroundColor: adminEmailVerified
                                ? "#10b981"
                                : "#4f46e5",
                              color: "white",
                              border: "none",
                              height: "42px",
                            }}
                          >
                            {adminEmailVerified ? (
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                Verified
                              </span>
                            ) : otpSending ? (
                              "Sending..."
                            ) : resendTimer > 0 ? (
                              `Resend (${resendTimer}s)`
                            ) : (
                              "Verify Email"
                            )}
                          </button>
                        )}
                      </div>
                      {adminEmailVerified && needsAdminEmailVerification && (
                        <p
                          style={{
                            marginTop: "4px",
                            fontSize: "12px",
                            color: "#059669",
                            fontWeight: 500,
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <svg
                            style={{ width: "14px", height: "14px" }}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Email verified successfully
                        </p>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Admin Phone</label>
                      <input
                        type="tel"
                        name="adminUser.phone"
                        value={formData.adminUser.phone}
                        onChange={handleInputChange}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{editingOrg ? "New Password" : "Password *"}</label>
                    <PasswordInput
                      name="adminUser.password"
                      value={formData.adminUser.password}
                      onChange={handleInputChange}
                      placeholder={
                        editingOrg
                          ? "Leave blank to keep current password"
                          : "Minimum 8 characters"
                      }
                      autoComplete="new-password"
                      required={!editingOrg}
                    />
                    <small className="form-hint">
                      {editingOrg
                        ? "The current password is never shown. Leave blank to keep it, or enter at least 8 characters to set a new one."
                        : "Password must be at least 8 characters long"}
                    </small>
                  </div>
                </div>
              )}

              {/* Address Section */}
              <div className="form-section">
                <h4>Address (Optional)</h4>
                <div className="form-group">
                  <label>Street</label>
                  <input
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    placeholder="123 Main St"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      placeholder="New York"
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                      placeholder="NY"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleInputChange}
                      placeholder="United States"
                    />
                  </div>
                  <div className="form-group">
                    <label>Zip Code</label>
                    <input
                      type="text"
                      name="address.zipCode"
                      value={formData.address.zipCode}
                      onChange={handleInputChange}
                      placeholder="10001"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Saving..."
                    : editingOrg
                      ? "Update Organization"
                      : "Create Organization"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Linked Workspaces — link/unlink this org's billing parent.
          DISABLED for now, feature planned for later. Commented out, not
          removed, per explicit request.
      {linkWorkspaceOrg && (
        <div className="modal-overlay" onClick={() => setLinkWorkspaceOrg(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Workspace Billing — {linkWorkspaceOrg.name}</h2>
              <button className="modal-close" onClick={() => setLinkWorkspaceOrg(null)}>
                ×
              </button>
            </div>
            <div className="modal-form">
              <p style={{ fontSize: "0.875rem", color: "#8ea1b8", marginBottom: "1rem" }}>
                Link this organization as a workspace under another org's subscription —
                it keeps its own isolated leads/users/data, but billing, plan, and seat
                limits are managed by the parent. Choose "None" to unlink.
              </p>
              <div className="form-group">
                <label>Billing Parent</label>
                <select
                  value={linkWorkspaceTarget}
                  onChange={(e) => setLinkWorkspaceTarget(e.target.value)}
                >
                  <option value="">None (independent organization)</option>
                  {linkWorkspaceCandidates.map((o) => (
                    <option key={o._id} value={o._id}>{o.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setLinkWorkspaceOrg(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={setBillingParentMutation.isPending}
                  onClick={() =>
                    setBillingParentMutation.mutate({
                      id: linkWorkspaceOrg._id,
                      billingParentOrganizationId: linkWorkspaceTarget || null,
                    })
                  }
                >
                  {setBillingParentMutation.isPending ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      */}

      {/* View Modal */}
      {viewingOrg && (
        <div className="modal-overlay" onClick={() => setViewingOrg(null)}>
          <div
            className="modal-content modal-view-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2 style={{ margin: 0 }}>{viewingOrg.name}</h2>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: "0.8125rem",
                  }}
                  className="view-modal-subtitle"
                >
                  {viewingOrg.slug} ·{" "}
                  <span
                    className={`badge ${getStatusBadge(viewingOrg.status)}`}
                  >
                    {viewingOrg.status}
                  </span>{" "}
                  · {viewingOrg.planId?.name || "Unknown"}
                </p>
              </div>
              <button
                className="modal-close"
                onClick={() => setViewingOrg(null)}
              >
                ×
              </button>
            </div>

            {/* Scrollable body */}
            <div className="view-details">
              {/* Organization Info */}
              <div className="detail-section">
                <h4>Organization</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Email</strong>
                    <span>{viewingOrg.email}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Phone</strong>
                    <span>{viewingOrg.phone || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Users</strong>
                    <span>
                      {viewingOrg.usage?.users || 0}
                      {viewingOrg.paidSeats
                        ? ` of ${viewingOrg.paidSeats} (plan seats)`
                        : ""}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Call Analysis</strong>
                    <span>
                      {(viewingOrg.callAnalysis?.mode || "manual") === "auto"
                        ? `Direct (auto) — ${viewingOrg.callAnalysis?.autoDailyLimit ?? 10}/day`
                        : `Manual — limit ${viewingOrg.callAnalysis?.autoDailyLimit ?? 10}/day`}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Created</strong>
                    <span>
                      {new Date(viewingOrg.createdAt).toLocaleDateString(
                        "en-IN",
                        { year: "numeric", month: "short", day: "numeric" },
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Subscription Details */}
              <div className="detail-section">
                <h4>Subscription</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <strong>Status:</strong>
                    <span
                      className={`badge ${getStatusBadge(viewingOrg.status)}`}
                    >
                      {viewingOrg.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Plan:</strong>
                    <span>{viewingOrg.planId?.name || "—"}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Billing Cycle:</strong>
                    <span>
                      {viewingOrg.billingCycle
                        ? viewingOrg.billingCycle.charAt(0).toUpperCase() +
                          viewingOrg.billingCycle.slice(1)
                        : "—"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Trial Start:</strong>
                    <span>
                      {formatDate(
                        viewingOrg.trialEndsAt
                          ? new Date(
                              new Date(viewingOrg.trialEndsAt).getTime() -
                                14 * 24 * 60 * 60 * 1000,
                            ).toISOString()
                          : null,
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Trial End:</strong>
                    <span>{formatDate(viewingOrg.trialEndsAt)}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Subscription Start:</strong>
                    <span>{formatDate(viewingOrg.subscriptionStartsAt)}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Subscription End:</strong>
                    <strong>Trial End</strong>
                    <span>{formatDate(viewingOrg.trialEndsAt)}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Subscription End</strong>
                    <span>{formatDate(viewingOrg.subscriptionEndsAt)}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Days Remaining</strong>
                    <span>
                      {(() => {
                        const info = getSubscriptionInfo(viewingOrg);
                        if (info.isExpired)
                          return (
                            <span className="badge badge-danger">Expired</span>
                          );
                        if (info.daysRemaining !== null) {
                          return (
                            <span
                              className={`badge ${info.daysRemaining <= 3 ? "badge-warning" : "badge-success"}`}
                            >
                              {info.daysRemaining} day
                              {info.daysRemaining !== 1 ? "s" : ""}
                            </span>
                          );
                        }
                        return (
                          <span className="badge badge-success">Active</span>
                        );
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Paid Seats:</strong>
                    <span>{viewingOrg.paidSeats ?? 1}</span>
                  </div>
                  {viewingOrg.pendingSeatsChange &&
                    viewingOrg.pendingSeatsChange !== viewingOrg.paidSeats && (
                      <div className="detail-item">
                        <strong>Pending Seats:</strong>
                        <span className="badge badge-warning">
                          {viewingOrg.pendingSeatsChange} (at next renewal)
                        </span>
                      </div>
                    )}
                  {viewingOrg.pendingDowngradePlan && (
                    <div className="detail-item">
                      <strong>Pending Downgrade:</strong>
                      <span className="badge badge-warning">
                        Scheduled for end of period
                      </span>
                    </div>
                  )}
                  {viewingOrg.pendingBillingCycle &&
                    viewingOrg.pendingBillingCycle !==
                      viewingOrg.billingCycle && (
                      <div className="detail-item">
                        <strong>Pending Cycle Switch:</strong>
                        <span className="badge badge-warning">
                          {viewingOrg.pendingBillingCycle} (at next renewal)
                        </span>
                      </div>
                    )}
                </div>
                {(() => {
                  const info = getSubscriptionInfo(viewingOrg);
                  if (info.isExpired) {
                    return (
                      <div style={{ marginTop: "0.75rem" }}>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            setViewingOrg(null);
                            openRenewModal(viewingOrg);
                          }}
                        >
                          Renew Subscription
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Admin User */}
              {viewingOrg.adminId ? (
                <div className="detail-section">
                  <h4>Admin User</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <strong>Name</strong>
                      <span>
                        {viewingOrg.adminId.firstName}{" "}
                        {viewingOrg.adminId.lastName}
                      </span>
                    </div>
                    <div className="detail-item">
                      <strong>Email</strong>
                      <span>{viewingOrg.adminId.email}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="detail-section">
                  <h4>Admin User</h4>
                  <div className="no-admin-warning">
                    <p>⚠️ No admin user created yet</p>
                    <button
                      className="btn btn-warning"
                      onClick={() => {
                        setViewingOrg(null);
                        openCreateAdminModal(viewingOrg);
                      }}
                    >
                      Create Admin User
                    </button>
                  </div>
                </div>
              )}

              {/* Notification History */}
              <div className="detail-section">
                <h4>📧 Notification History</h4>
                {viewingOrg.notificationLogs &&
                viewingOrg.notificationLogs.length > 0 ? (
                  <div className="history-table-wrapper">
                    <table className="data-table history-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Days Left</th>
                          <th>Sent At</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingOrg.notificationLogs.map((log) => {
                          const typeLabels = {
                            expiring_5: "5 Days Left",
                            expiring_4: "4 Days Left",
                            expiring_3: "3 Days Left",
                            expiring_2: "2 Days Left",
                            expiring_1: "1 Day Left",
                            expiring_today: "Expires Today",
                            expired: "Expired",
                          };
                          const typeBadges = {
                            expiring_5: "badge-info",
                            expiring_4: "badge-info",
                            expiring_3: "badge-warning",
                            expiring_2: "badge-warning",
                            expiring_1: "badge-danger",
                            expiring_today: "badge-danger",
                            expired: "badge-danger",
                          };
                          return (
                            <tr key={log._id}>
                              <td>
                                <span
                                  className={`badge ${typeBadges[log.type] || "badge-info"}`}
                                >
                                  {typeLabels[log.type] || log.type}
                                </span>
                              </td>
                              <td>
                                {log.daysLeft === -1 ? "—" : log.daysLeft}
                              </td>
                              <td>
                                {new Date(
                                  log.sentAt || log.createdAt,
                                ).toLocaleString("en-IN", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </td>
                              <td>
                                {log.emailSent ? (
                                  <span className="badge badge-success">
                                    ✅ Sent
                                  </span>
                                ) : (
                                  <span
                                    className="badge badge-danger"
                                    title={log.error || "Failed"}
                                  >
                                    ❌ Failed
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data-text">
                    No notification emails sent yet
                  </p>
                )}
              </div>

              {/* Subscription/Payment History */}
              <div className="detail-section">
                <h4>💳 Subscription History</h4>
                {viewingOrg.subscriptions &&
                viewingOrg.subscriptions.length > 0 ? (
                  <div className="history-table-wrapper">
                    <table className="data-table history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Plan</th>
                          <th>Telecallers</th>
                          <th>Cycle</th>
                          <th>Period</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingOrg.subscriptions.map((sub) => {
                          const statusBadges = {
                            active: "badge-success",
                            past_due: "badge-warning",
                            cancelled: "badge-danger",
                            expired: "badge-secondary",
                            trial: "badge-info",
                            pending: "badge-warning",
                            failed: "badge-danger",
                          };
                          const statusLabels = {
                            active: "Paid",
                            past_due: "Past Due",
                            cancelled: "Cancelled",
                            expired: "Expired",
                            trial: "Trial",
                            pending: "Pending",
                            failed: "Failed",
                          };
                          const formatAmount = (amt) => {
                            if (!amt && amt !== 0) return "—";
                            return `₹${Number(amt).toLocaleString("en-IN")}`;
                          };
                          return (
                            <tr key={sub._id}>
                              <td>
                                {new Date(sub.createdAt).toLocaleDateString(
                                  "en-IN",
                                  {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )}
                              </td>
                              <td>
                                {sub.planId?.name || sub.notes?.planSlug || "—"}
                              </td>
                              <td>
                                {sub.notes?.numUsers
                                  ? `${sub.notes.numUsers} telecaller${sub.notes.numUsers > 1 ? "s" : ""}`
                                  : sub.totalAmount && sub.amount
                                    ? "—"
                                    : "—"}
                              </td>
                              <td>
                                {sub.billingCycle === "yearly"
                                  ? "Yearly"
                                  : sub.billingCycle === "monthly"
                                    ? "Monthly"
                                    : "—"}
                              </td>
                              <td>
                                {sub.currentPeriodStart ||
                                sub.currentPeriodEnd ? (
                                  <>
                                    {formatDate(sub.currentPeriodStart)} —{" "}
                                    {formatDate(sub.currentPeriodEnd)}
                                  </>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td>
                                {formatAmount(sub.totalAmount || sub.amount)}
                              </td>
                              <td>
                                <span
                                  className={`badge ${statusBadges[sub.status] || "badge-secondary"}`}
                                >
                                  {statusLabels[sub.status] || sub.status}
                                </span>
                              </td>
                              <td>
                                {sub.payments && sub.payments.length > 0 ? (
                                  <span className="badge badge-success">
                                    {sub.payments[0].method ||
                                      sub.paymentProvider ||
                                      "Online"}
                                  </span>
                                ) : (
                                  <span className="badge badge-secondary">
                                    —
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data-text">No subscription records yet</p>
                )}
                {/* API Access Section */}
                <div className="detail-section detail-section-api">
                  <OrganizationsApiKeys orgId={viewingOrg?._id} />
                </div>
              </div>

              {/* Sticky footer */}
              <div className="modal-actions modal-actions-view">
                <button
                  className="btn btn-outline"
                  onClick={() => setViewingOrg(null)}
                >
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setViewingOrg(null);
                    openEditModal(viewingOrg);
                  }}
                >
                  Edit Organization
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {showEmailModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowEmailModal(false);
            setEmailSubject("");
            setEmailBody("");
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "600px" }}
          >
            <div className="modal-header">
              <h2>✉️ Send Email to Organization Admins</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailSubject("");
                  setEmailBody("");
                }}
              >
                ×
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!emailSubject.trim() || !emailBody.trim()) {
                  toast.error("Subject and email body are required");
                  return;
                }
                sendEmailMutation.mutate({
                  subject: emailSubject.trim(),
                  htmlBody: emailBody.trim(),
                });
              }}
              className="modal-form"
            >
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Email Body *</label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Write your email content here... (HTML is supported)"
                  rows={10}
                  className="email-body-textarea"
                  required
                />
                <small className="email-hint">
                  You can use HTML tags for formatting. The email will be
                  wrapped in a branded template.
                </small>
              </div>

              <div className="email-info-box">
                <p>
                  📧 This email will be sent to all active organization
                  administrators.
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.5rem",
                }}
              >
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailSubject("");
                    setEmailBody("");
                  }}
                  disabled={sendEmailMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    sendEmailMutation.isPending ||
                    !emailSubject.trim() ||
                    !emailBody.trim()
                  }
                >
                  {sendEmailMutation.isPending ? "Sending..." : "Send Email"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowOtpModal(false);
            setOtp("");
            setOtpError("");
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "420px" }}
          >
            <div className="modal-header">
              <h2 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <HiOutlineMail
                  style={{ width: "22px", height: "22px", color: "#4f46e5" }}
                />
                Verify Email
              </h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowOtpModal(false);
                  setOtp("");
                  setOtpError("");
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: "1.5rem", textAlign: "center" }}>
              <p
                style={{
                  marginBottom: "1rem",
                  color: "#374151",
                  fontSize: "0.9rem",
                }}
              >
                We&apos;ve sent a 6-digit verification code to{" "}
                <strong>{formData.adminUser.email}</strong>
              </p>
              {otpError && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    padding: "0.75rem",
                    marginBottom: "1rem",
                    color: "#991b1b",
                    fontSize: "0.85rem",
                  }}
                >
                  {otpError}
                </div>
              )}
              <input
                type="text"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setOtpError("");
                }}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  fontSize: "1.5rem",
                  textAlign: "center",
                  letterSpacing: "0.5em",
                  border: "2px solid #ddd",
                  borderRadius: "8px",
                  outline: "none",
                  fontWeight: "600",
                }}
                autoFocus
              />
              <div
                style={{
                  marginTop: "1.5rem",
                  display: "flex",
                  gap: "0.5rem",
                  justifyContent: "center",
                }}
              >
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowOtpModal(false);
                    setOtp("");
                    setOtpError("");
                  }}
                  disabled={otpVerifying}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleVerifyAdminOtp}
                  disabled={otpVerifying || otp.length !== 6}
                  className="btn btn-primary"
                  style={{
                    opacity: otpVerifying || otp.length !== 6 ? 0.5 : 1,
                  }}
                >
                  {otpVerifying ? "Verifying..." : "Verify OTP"}
                </button>
              </div>
              <div
                style={{
                  marginTop: "1rem",
                  fontSize: "0.85rem",
                  color: "#6b7280",
                }}
              >
                {resendTimer > 0 ? (
                  <span>Resend OTP in {resendTimer}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendAdminOtp}
                    disabled={otpSending}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#4f46e5",
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontSize: "0.85rem",
                      padding: 0,
                    }}
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom confirm dialog — replaces window.confirm for suspend/activate/delete/direct-login */}
      {confirmModal && (
        <div className="modal-overlay" onClick={() => setConfirmModal(null)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "440px" }}
          >
            <div className="modal-header">
              <h2>{confirmModal.title}</h2>
              <button className="modal-close" onClick={() => setConfirmModal(null)}>
                ×
              </button>
            </div>
            <div className="modal-form">
              <p className="confirm-modal-message">
                {confirmModal.message}
              </p>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setConfirmModal(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${confirmModal.danger ? "btn-danger" : "btn-primary"}`}
                  onClick={() => {
                    confirmModal.onConfirm();
                    setConfirmModal(null);
                  }}
                >
                  {confirmModal.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-large {
          max-width: 800px;
        }
        .form-section {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }
        .form-section h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 1rem;
        }
        .admin-section {
          background: #f9fafb;
          margin: 1.5rem -1.5rem 0;
          padding: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }
        .section-hint {
          font-size: 0.75rem;
          color: #6b7280;
          margin-bottom: 1rem;
        }
        .confirm-modal-message {
          margin: 0;
          font-size: 0.9rem;
          line-height: 1.5;
          color: #374151;
        }
        .form-hint {
          display: block;
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }
        .admin-info {
          display: flex;
          flex-direction: column;
        }
        .admin-name {
          font-weight: 500;
        }
        .admin-email {
          font-size: 0.75rem;
          color: #6b7280;
        }
        .no-admin {
          color: #9ca3af;
          font-style: italic;
          font-size: 0.875rem;
        }
        .btn-warning {
          background: #f59e0b;
          color: white;
          font-size: 0.75rem;
        }
        .btn-warning:hover:not(:disabled) {
          background: #d97706;
        }
        .btn-success {
          background: #10b981;
          color: white;
          font-size: 0.75rem;
        }
        .btn-success:hover:not(:disabled) {
          background: #059669;
        }
        .modal-view-content {
          display: flex;
          flex-direction: column;
          max-height: 90vh;
        }
        .modal-view-content .modal-header {
          flex-shrink: 0;
        }
        .modal-view-content .view-details {
          flex: 1;
          overflow-y: auto;
          padding: 1.5rem;
        }
        .modal-view-content .modal-actions-view {
          flex-shrink: 0;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
          padding: 1rem 1.5rem;
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
        }
        .view-details {
          padding: 1.5rem;
        }
        .detail-section {
          margin-bottom: 1.5rem;
        }
        .detail-section-api {
          border-top: 1px solid #e5e7eb;
          padding-top: 1rem;
          margin-top: 1.5rem;
          margin-bottom: 0;
        }
        .detail-section h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }
        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .detail-item strong {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
        }
        .detail-item span {
          font-size: 0.875rem;
          color: #111827;
        }
        .no-admin-warning {
          background: #fef3c7;
          padding: 1rem;
          border-radius: 0.5rem;
          text-align: center;
        }
        .no-admin-warning p {
          color: #92400e;
          margin-bottom: 0.75rem;
        }
        .subscription-info {
          font-size: 0.8rem;
        }
        .subscription-info .sub-date {
          font-size: 0.7rem;
          color: #6b7280;
          margin-top: 2px;
        }
        .badge-success {
          background: #d1fae5;
          color: #065f46;
        }
        .badge-warning {
          background: #fef3c7;
          color: #92400e;
        }
        .badge-danger {
          background: #fee2e2;
          color: #991b1b;
        }
        .badge-info {
          background: #dbeafe;
          color: #1e40af;
        }
        .badge-secondary {
          background: #f3f4f6;
          color: #6b7280;
        }
        .badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        @media (max-width: 640px) {
          .detail-grid {
            grid-template-columns: 1fr;
          }
        }

        /* ===== Dark theme — View / detail modals ===== */
        html.dark .view-modal-subtitle { color: #aebdce; }
        html.dark .modal-view-content .modal-actions-view {
          background: #131833;
          border-top-color: #2a3158;
        }
        html.dark .view-details .detail-section h4 {
          color: #f1f5f9;
          border-bottom-color: #2a3158;
        }
        html.dark .detail-section-api {
          border-top-color: #2a3158;
        }
        html.dark .detail-item strong { color: #8ea1b8; }
        html.dark .detail-item span { color: #e2e8f0; }
        html.dark .subscription-info .sub-date { color: #8ea1b8; }
        html.dark .no-admin-warning {
          background: rgba(245, 158, 11, 0.12);
        }
        html.dark .no-admin-warning p { color: #fbbf24; }

        /* Dark: create-admin / edit modal (same navy family) */
        html.dark .form-section {
          border-top-color: #2a3158;
        }
        html.dark .form-section h4 { color: #cbd5e1; }
        html.dark .admin-section {
          background: #131833;
          border-top-color: #2a3158;
        }
        html.dark .section-hint,
        html.dark .form-hint,
        html.dark .admin-email { color: #8ea1b8; }
        html.dark .confirm-modal-message { color: #cbd5e1; }
        html.dark .no-admin { color: #64748b; }

        /* Renew subscription modal — info box (was inline light styles) */
        .renew-info {
          background: #f9fafb;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }
        .renew-info-label {
          color: #6b7280;
          font-size: 0.875rem;
        }
        html.dark .renew-info {
          background: #131833;
          border: 1px solid #2a3158;
          color: #e2e8f0;
        }
        html.dark .renew-info-label { color: #8ea1b8; }

        /* Send Email modal — textarea, hint, info box (was inline light styles) */
        .email-body-textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 0.875rem;
          resize: vertical;
          min-height: 200px;
          background: #fff;
          color: #111827;
        }
        .email-body-textarea:focus {
          outline: none;
          border-color: #6366f1;
        }
        .email-hint {
          color: #666;
          font-size: 0.75rem;
        }
        .email-info-box {
          background: #f0f4ff;
          padding: 0.75rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }
        .email-info-box p {
          margin: 0;
          font-size: 0.875rem;
          color: #4338ca;
        }
        html.dark .email-body-textarea {
          background: #0c1226;
          border-color: #2a3158;
          color: #e5e7eb;
        }
        html.dark .email-body-textarea::placeholder { color: #5b6b84; }
        html.dark .email-body-textarea:focus { border-color: #4f46e5; }
        html.dark .email-hint { color: #8ea1b8; }
        html.dark .email-info-box {
          background: rgba(139, 92, 246, 0.15);
          border: 1px solid rgba(139, 92, 246, 0.4);
        }
        html.dark .email-info-box p { color: #a5b4fc; }
      `}</style>
    </div>
  );
};

export default OrganizationsList;
